import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/context/CartContext";
import { ConsentProvider } from "@/context/ConsentContext";
import { SeoProvider } from "@/context/SeoContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import PendingOrdersBanner from "@/components/PendingOrdersBanner";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import BackToTop from "@/components/BackToTop";
import DiscoverStrip from "@/components/DiscoverStrip";
import RecentlyViewed from "@/components/RecentlyViewed";
import CartDrawer from "@/components/CartDrawer";
import HomePage from "@/pages/HomePage";
import BoutiquePage from "@/pages/BoutiquePage";
import ProductPage from "@/pages/ProductPage";
import HistoirePage from "@/pages/HistoirePage";
import GazettePage from "@/pages/GazettePage";
import ArticlePage from "@/pages/ArticlePage";
import ContactPage from "@/pages/ContactPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentSupplementPage from "@/pages/PaymentSupplementPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import CustomerAuthPage from "@/pages/CustomerAuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CustomerAccountPage from "@/pages/CustomerAccountPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminReviews from "@/pages/admin/AdminReviews";
import ReviewPage from "@/pages/ReviewPage";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBlogForm from "@/pages/admin/AdminBlogForm";
import AdminAgenda from "@/pages/admin/AdminAgenda";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminLoyalty from "@/pages/admin/AdminLoyalty";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminWaitlist from "@/pages/admin/AdminWaitlist";
import AdminSpotlight from "@/pages/admin/AdminSpotlight";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminStores from "@/pages/admin/AdminStores";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminDeliveries from "@/pages/admin/AdminDeliveries";
import RelativeOrderPage from "@/pages/RelativeOrderPage";
import EcoleParent from "@/pages/EcoleParent";
import EcoleResponsable from "@/pages/EcoleResponsable";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import LegalPage from "@/pages/LegalPage";
import ReferralCapture from "@/components/ReferralCapture";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function CookieConsentGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <CookieConsent />;
}

function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <ConsentProvider>
          <SeoProvider>
            <BrowserRouter>
          <ScrollToTop />
          <ReferralCapture />
          <div className="flex min-h-screen flex-col bg-cream font-sans text-bark">
            <Header />
            <PendingOrdersBanner />
            <DiscoverStrip />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/boutique" element={<BoutiquePage />} />
                <Route path="/produit/:productId" element={<ProductPage />} />
                <Route path="/histoire" element={<HistoirePage />} />
                <Route path="/gazette" element={<GazettePage />} />
                <Route path="/gazette/:slug" element={<ArticlePage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/mentions-legales" element={<LegalPage docId="mentions-legales" />} />
                <Route path="/confidentialite" element={<LegalPage docId="confidentialite" />} />
                <Route path="/cookies" element={<LegalPage docId="cookies" />} />
                <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
                <Route path="/paiement/complement" element={<PaymentSupplementPage />} />
                <Route path="/avis/:token" element={<ReviewPage />} />
                <Route path="/paiement/annule" element={<PaymentCancelPage />} />
                <Route path="/compte/connexion" element={<CustomerAuthPage />} />
                <Route path="/compte/reinitialiser" element={<ResetPasswordPage />} />
                <Route path="/compte" element={<CustomerAccountPage />} />
                <Route path="/ecole/partage/:token" element={<RelativeOrderPage />} />
                <Route path="/ecole/:slug" element={<EcoleParent />} />
                <Route path="/ecole-responsable" element={<EcoleResponsable />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="accueil" element={<AdminSpotlight />} />
                  <Route path="produits" element={<AdminProducts />} />
                  <Route path="produits/nouveau" element={<AdminProductForm />} />
                  <Route path="produits/:productId" element={<AdminProductForm />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="medias" element={<AdminMedia />} />
                  <Route path="commandes" element={<AdminOrders />} />
                  <Route path="avis" element={<AdminReviews />} />
                  <Route path="abonnements" element={<AdminSubscriptions />} />
                  <Route path="magasins" element={<AdminStores />} />
                  <Route path="ecoles" element={<AdminSchools />} />
                  <Route path="livraison" element={<AdminShipping />} />
                  <Route path="tournees" element={<AdminDeliveries />} />
                  <Route path="gazette" element={<AdminBlog />} />
                  <Route path="gazette/nouveau" element={<AdminBlogForm />} />
                  <Route path="gazette/:slug" element={<AdminBlogForm />} />
                  <Route path="agenda" element={<AdminAgenda />} />
                  <Route path="tarifs" element={<AdminPricing />} />
                  <Route path="fidelite" element={<AdminLoyalty />} />
                  <Route path="clients" element={<AdminCustomers />} />
                  <Route path="clients/:customerId" element={<AdminCustomers />} />
                  <Route path="newsletter" element={<AdminNewsletter />} />
                  <Route path="legal" element={<AdminLegal />} />
                  <Route path="liste-attente" element={<AdminWaitlist />} />
                  <Route path="seo" element={<AdminSeo />} />
                  <Route path="securite" element={<AdminSecurity />} />
                </Route>
                <Route path="*" element={<HomePage />} />
              </Routes>
            </main>
            <RecentlyViewed />
            <Footer />
            <CookieConsentGate />
          </div>
          <CartDrawer />
          <BackToTop />
          <Toaster position="top-center" richColors />
            </BrowserRouter>
          </SeoProvider>
        </ConsentProvider>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
